import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { cn } from '@/lib/utils';

function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' | 'night' {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

function getGreeting(timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night'): string {
  switch (timeOfDay) {
    case 'morning': return 'Good morning';
    case 'afternoon': return 'Good afternoon';
    case 'evening': return 'Good evening';
    case 'night': return 'Good night';
  }
}

interface HeroGreetingProps {
  className?: string;
}

export function HeroGreeting({ className }: HeroGreetingProps) {
  const { user } = useCurrentUser();
  const author = useAuthor(user?.pubkey);
  const metadata = author.data?.metadata;
  
  const timeOfDay = useMemo(() => getTimeOfDay(), []);
  const greeting = getGreeting(timeOfDay);

  const displayName = metadata?.display_name || metadata?.name;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={cn('px-6 py-6', className)}
    >
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
        <span className="text-foreground">{greeting}</span>
        {displayName && (
          <>
            <span className="text-muted-foreground">, </span>
            <span className="bg-gradient-to-r from-primary via-emerald-400 to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              {displayName}
            </span>
          </>
        )}
        {!displayName && user && (
          <span className="text-muted-foreground">!</span>
        )}
      </h1>
    </motion.div>
  );
}
